import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';
import MUIDataTable from 'mui-datatables';
import { Alert, Box, Button } from '@mui/material';
import AppBarButtonLink from 'components/shared/buttons/AppBarButtonLink';
import ClientPaymentFilters from 'components/client-payment/ClientPaymentFilters';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import PaymentExpandableRow from 'components/shared/table-rows/PaymentExpandableRow';
import PaymentProcessingModal from 'components/shared/modals/PaymentProcessingModal';
import PlanSelectionModal from 'components/shared/modals/PlanSelectionModal';
import MySubscriptionCard from 'components/client-payment/MySubscriptionCard';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import { membershipStatus } from 'common/constants/options';
import {
  clientPaymentHistoryFields,
  clientPaymentListFields
} from 'common/constants/query-fields/clientPayment';
import routes from 'common/routes';
import {
  getClientPaymentColumns,
  getMyClientPaymentColumns
} from 'common/table/columns/clientPayment';
import { getClientPaymentData, getMyClientPaymentData } from 'common/table/data/clientPayment';
import { defaultTableOptions, getSelectToolBar, tableLocalization } from 'common/table/utils';
import { canModify, hasAdminAccess } from 'common/utils/user';
import AppBarContext from 'context/AppBarContext';
import EchoContext from 'context/EchoContext';
import clientPaymentSlice from 'store/modules/clientPayment';
import uiSlice from 'store/modules/ui';
import { createSnack } from 'common/utils/general';
import planApi from 'api/graphql/plan';
import paymentApi from 'api/rest/payment';
import usePaymentProcessing from 'hooks/payments/usePaymentProcessing';
import useIpGeolocation from 'hooks/payments/useIpGeolocation';

function ClientPaymentList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['buttons', 'labels', 'routes', 'tables', 'payments']);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const echoContext = useContext(EchoContext);
  const echo = echoContext?.echo || null;
  const adminChannelRef = useRef(null);

  const filters = useSelector((state) => state.clientPayment.filters);
  const pagination = useSelector((state) => state.clientPayment.pagination);
  const paginatorInfo = useSelector((state) => state.clientPayment.paginatorInfo);
  const payments = useSelector((state) => state.clientPayment.data.payments);
  const refresh = useSelector((state) => state.clientPayment.refresh);
  const {
    role,
    id: userId,
    membership_status: userMembershipStatus,
    plan_id: userPlan,
    country: userCountry,
    platform: userPlatform
  } = useSelector((state) => state.auth.user);
  const status = useSelector((state) => state.clientPayment.status.general);

  // Estado para el modal de selección de planes
  const [planSelectionOpen, setPlanSelectionOpen] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Detección de moneda/gateway por IP del usuario
  const { currency: detectedCurrency, gateway: detectedGateway, gatewayEnabled } = useIpGeolocation();

  // Moneda de facturación: el país del PERFIL manda. Un colombiano cobra en COP (Wompi) aunque la
  // IP lo detecte como USD (dev/proxy) — antes eso ponía USD, Stripe está apagado, y el banner de
  // "Selecciona planes" desaparecía. Resto de países: usa la moneda detectada por IP.
  const userCurrency = useMemo(() => {
    let isColombian = false;
    try {
      const country = JSON.parse(userCountry);
      isColombian = country?.label === 'Colombia' || country?.value === 'CO';
    } catch {
      isColombian = false;
    }
    if (isColombian) return 'COP';
    return detectedCurrency || 'USD';
  }, [detectedCurrency, userCountry]);

  // Hook para manejar el procesamiento del pago con Wompi
  const {
    modalOpen,
    status: paymentStatus,
    timeRemaining,
    showPaymentInfo,
    startMultiPlanProcessing,
    openProcessingModal,
    retryPayment,
    closeModal,
    dismissPaymentInfo
  } = usePaymentProcessing(userId);

  // Plan preseleccionado del modal de planes
  const [modalPlanId, setModalPlanId] = useState(null);

  // ¿Mostrar UI de pago? COP (Wompi) o USD con Stripe habilitado.
  const canShowPaymentUI =
    userCurrency === 'COP' || (userCurrency === 'USD' && detectedGateway === 'stripe' && gatewayEnabled);

  const hasAdminView = hasAdminAccess(role);
  const canModifyData = canModify(role);
  const titlei18nKey = hasAdminView ? 'payment.client.list' : 'payment.myPayments';
  const pageTitle = t(titlei18nKey, { ns: 'routes' });
  const emptyMessage = t('empty', { type: pageTitle.toLowerCase(), ns: 'tables' });

  const appBarButton = useMemo(
    () =>
      canModifyData ? (
        <AppBarButtonLink
          label={t('add', { ns: 'buttons' })}
          path={routes.createClientPayment.path}
        />
      ) : null,
    [canModifyData, t]
  );

  useEffect(() => {
    const updatedFilters = { ...filters };
    let updatedFields = clientPaymentListFields;

    if (!hasAdminView) {
      updatedFields = clientPaymentHistoryFields;
      updatedFilters.user_id = userId;
    }

    dispatch(
      clientPaymentSlice.actions.list({
        fields: updatedFields,
        filters: updatedFilters,
        pagination
      })
    );
  }, [filters, pagination, refresh, hasAdminView, userId, dispatch]);

  useEffect(() => {
    setAppBarButton(appBarButton);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle, appBarButton]);

  // WebSocket listener for admin - refresh table when new payment is created
  useEffect(() => {
    // Only subscribe if admin view and echo is available
    if (hasAdminView && echo && echo.private && typeof echo.private === 'function') {
      try {
        adminChannelRef.current = echo.private('admin');

        adminChannelRef.current.listen('.payment.created', () => {
          // Trigger table refresh when a new payment is created
          dispatch(clientPaymentSlice.actions.toggleRefresh());
        });
      } catch (err) {
        // Silent fail - WebSocket is optional enhancement
      }
    }

    return () => {
      // Cleanup subscription
      if (adminChannelRef.current) {
        try {
          adminChannelRef.current.stopListening('.payment.created');
          if (echo) echo.leave('private-admin');
        } catch (err) {
          // Silent fail on cleanup
        }
        adminChannelRef.current = null;
      }
    };
  }, [hasAdminView, echo, dispatch]);

  // Cargar todos los planes disponibles cuando se abre el modal
  const loadPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const response = await planApi.listAll();
      const plans = response.data?.allPlans || [];
      setAvailablePlans(plans);
    } catch {
      setAvailablePlans([]);
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  // Abrir modal de selección de planes (opcionalmente preseleccionando un plan, p. ej. al renovar)
  const openPlanSelection = useCallback((planId = null) => {
    setModalPlanId(planId || userPlan?.id || null);
    setPlanSelectionOpen(true);
    loadPlans();
  }, [loadPlans, userPlan]);

  // Detectar redirect de Wompi o acción de selección de plan al cargar la página
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const url = new URL(window.location.href);

    if (urlParams.get('payment_gateway') === 'wompi' && !hasAdminView) {
      url.searchParams.delete('payment_gateway');
      url.searchParams.delete('id');
      window.history.replaceState({}, '', url.toString());
      openProcessingModal();
    } else if (urlParams.get('action') === 'renew' && !hasAdminView) {
      const planId = urlParams.get('plan_id');
      url.searchParams.delete('action');
      url.searchParams.delete('plan_id');
      window.history.replaceState({}, '', url.toString());
      // Embudar la renovación al checkout unificado (único / suscripción)
      if (planId) openPlanSelection(planId);
    } else if (urlParams.get('action') === 'select-plan' && !hasAdminView) {
      url.searchParams.delete('action');
      window.history.replaceState({}, '', url.toString());
      openPlanSelection();
    } else if (urlParams.get('action') === 'retry' && !hasAdminView) {
      // Llega desde el email de cobro fallido: la card "Mi suscripción" ya ofrece
      // "Reintentar pago" / "Actualizar tarjeta". Solo limpiamos el parámetro.
      url.searchParams.delete('action');
      window.history.replaceState({}, '', url.toString());
    }
  }, [openProcessingModal, openPlanSelection, hasAdminView]);

  // Cerrar modal de selección de planes
  const closePlanSelection = useCallback(() => {
    setPlanSelectionOpen(false);
  }, []);

  // Manejar confirmación de planes seleccionados
  const handlePlanConfirm = useCallback(
    (planIds, couponCode = null) => {
      closePlanSelection();
      startMultiPlanProcessing(planIds, couponCode);
    },
    [closePlanSelection, startMultiPlanProcessing]
  );

  // Pago rápido del plan actual → abre el checkout unificado (deja elegir único / suscripción)
  const handleQuickPayment = useCallback(() => {
    if (userPlan?.id) openPlanSelection(userPlan.id);
  }, [userPlan, openPlanSelection]);

  // Suscripción desde el modal: COP → checkout de tarjeta; USD → Stripe hosted.
  const handleSubscribe = useCallback(
    (planId, couponCode = null) => {
      closePlanSelection();

      if (userCurrency === 'USD' && detectedGateway === 'stripe' && gatewayEnabled) {
        paymentApi
          .createStripeSubscriptionCheckout({ plan_id: planId })
          .then(({ url }) => { window.location.href = url; })
          .catch((err) => {
            dispatch(
              uiSlice.actions.enqueueSnackbar(
                createSnack({
                  key: 'stripe-sub-error',
                  variant: 'error',
                  message: err?.message || t('subscription.failed.message', {
                    ns: 'payments',
                    defaultValue: 'No pudimos iniciar la suscripción.'
                  })
                })
              )
            );
          });
        return;
      }

      // COP (Wompi): pantalla de checkout dedicada (2 columnas). El cupón del modal se pre-aplica allí.
      navigate(`/checkout/subscribe/${planId}${couponCode ? `?coupon=${encodeURIComponent(couponCode)}` : ''}`);
    },
    [userCurrency, detectedGateway, gatewayEnabled, closePlanSelection, navigate, dispatch, t]
  );

  const tableData = hasAdminView
    ? getClientPaymentData(payments, t, dateLocale)
    : getMyClientPaymentData(payments, t, dateLocale);

  const columns = hasAdminView
    ? getClientPaymentColumns({ data: tableData, role, translateFunc: t })
    : getMyClientPaymentColumns({ data: tableData, translateFunc: t });

  const onChangePage = (page) => {
    dispatch(clientPaymentSlice.actions.updatePagination({ page: page + 1 }));
  };

  const onDelete = (ids) => {
    dispatch(clientPaymentSlice.actions.remove(ids));
  };

  const onRestore = (ids) => {
    dispatch(clientPaymentSlice.actions.restore(ids));
  };

  const options = {
    ...defaultTableOptions,
    count: paginatorInfo.total,
    customToolbarSelect: (selectedRows, displayData) =>
      getSelectToolBar({
        filters,
        rows: selectedRows,
        data: displayData,
        onDelete,
        onRestore
      }),
    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: true,
    onChangePage,
    page: pagination.page - 1,
    renderExpandableRow: (rowData, rowMeta) => (
      <PaymentExpandableRow data={rowData} meta={rowMeta} role={role} tableData={tableData} />
    ),
    rowsPerPage: pagination.first,
    selectableRows: canModifyData ? 'multiple' : 'none',
    selectableRowsHeader: canModifyData,
    textLabels: tableLocalization(t('payment.payments', { ns: 'routes' }).toLowerCase(), t)
  };

  const PAYMENTS_ENABLED = true;

  const isActiveMember = userMembershipStatus === membershipStatus.active;
  // Mensaje del banner de planes según el estado: el activo puede SUMAR planes adicionales.
  const planSelectMessage = () => {
    if (isActiveMember) {
      return t('planSelect.activeMessage', { ns: 'payments', defaultValue: 'Tu plan está activo. Selecciona un plan adicional y lleva tu experiencia OTRIX al siguiente nivel.' });
    }
    if (userMembershipStatus === membershipStatus.inArrears) {
      return t('planSelect.arrearsMessage', { ns: 'payments', defaultValue: 'Continúa disfrutando de tu plan, o suma uno de nuestros planes adicionales para potenciar tus resultados.' });
    }
    return t('planSelect.inactiveMessage', { ns: 'payments', defaultValue: 'No cuentas con una membresía activa. Activa tu plan o disfruta de nuestros planes adicionales para empezar hoy.' });
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      {hasAdminView && <ClientPaymentFilters />}
      {/* Alerta informativa sobre el enlace de pago — solo COP */}
      {PAYMENTS_ENABLED && !hasAdminView && userCurrency === 'COP' && showPaymentInfo && userMembershipStatus !== membershipStatus.active && (
        <Alert
          sx={{
            mb: 2,
            alignItems: 'center'
          }}
          variant="filled"
          severity="info"
          action={
            <Button onClick={dismissPaymentInfo} size="small">
              {t('dismissInfo', { ns: 'payments' })}
            </Button>
          }>
          {t('paymentLinkInfo', { ns: 'payments' })}
        </Alert>
      )}
      {/* Alerta de membresía inactiva con botones de pago — COP o USD+Stripe.
          Se muestra aunque no haya plan activo (tras cancelar / sin pagos) para poder reactivar. */}
      {PAYMENTS_ENABLED && !hasAdminView && canShowPaymentUI && (
        <Alert
          sx={{ mb: 2 }}
          variant="filled"
          severity="info"
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {userPlan && !isActiveMember && (
                <Button onClick={handleQuickPayment} size="small" variant="contained">
                  {t('pay', { ns: 'payments' })}
                </Button>
              )}
              <Button onClick={() => openPlanSelection()} size="small" variant="outlined">
                {isActiveMember
                  ? t('selectPlans.addonTitle', { ns: 'payments', defaultValue: 'Planes adicionales' })
                  : t('selectPlans.title', { ns: 'payments' })}
              </Button>
            </Box>
          }>
          {planSelectMessage()}
        </Alert>
      )}

      {/* Tarjeta "Mi suscripción" — solo cliente (no admin) */}
      {!hasAdminView && <MySubscriptionCard onResubscribe={() => openPlanSelection()} />}

      {/* Modal de selección de planes — checkout unificado (único / suscripción) */}
      <PlanSelectionModal
        open={planSelectionOpen}
        onClose={closePlanSelection}
        onConfirm={handlePlanConfirm}
        onSubscribe={handleSubscribe}
        plans={availablePlans}
        loading={loadingPlans}
        currentPlanId={modalPlanId}
        userCurrency={userCurrency}
        userPlatform={userPlatform}
        gateway={detectedGateway}
        gatewayEnabled={gatewayEnabled}
        membershipStatus={userMembershipStatus}
      />

      {/* Modal de procesamiento de pago híbrido */}
      <PaymentProcessingModal
        open={modalOpen}
        status={paymentStatus}
        timeRemaining={timeRemaining}
        onClose={closeModal}
        onRetryPayment={() => retryPayment()}
      />
      <Box
        sx={{
          position: 'relative'
        }}>
        <LoadingBlock
          emptyBlockProps={{ message: emptyMessage }}
          isValid={payments.length > 0}
          skeletonProps={{ rows: 5 }}
          status={status}
          type="table">
          <MUIDataTable
            className="clientPayment"
            columns={columns}
            data={tableData}
            options={options}
          />
        </LoadingBlock>
      </Box>
    </>
  );
}

export default ClientPaymentList;
